import { cloneValue, getAtPath, setAtPath } from './configPaths.js'

export function isSchemaV2(schema) {
  return schema?.dialect === 'studio-editor-v2/schema-v2'
}

export function collectionById(schema, collectionId) {
  return (schema?.collections || []).find((entry) => entry.id === collectionId) || null
}

export function mediaReferences(schema, configuration) {
  if (!isSchemaV2(schema)) return []
  return imageFieldReferences(schema, configuration)
}

/**
 * Every image-editor field in a schema, as { path, profile }.
 *
 * Dialect-agnostic: schema-v1 and schema-v2 declare image fields with the same shape
 * (top-level section fields plus collection itemSections), so the walk is shared.
 * `mediaReferences` keeps its v2-only guard for existing callers.
 */
export function imageFieldReferences(schema, configuration) {
  const references = []
  ;(schema.sections || []).forEach((section) => {
    ;(section.fields || []).forEach((field) => {
      if (field.type === 'image-editor') {
        references.push({ path: field.path, profile: field.profile })
      }
    })
  })
  ;(schema.collections || []).forEach((collection) => {
    const items = getAtPath(configuration, collection.path, [])
    items.forEach((item, index) => {
      ;(collection.itemSections || []).forEach((section) => {
        ;(section.fields || []).forEach((field) => {
          if (field.type === 'image-editor') {
            references.push({
              path: `${collection.path}.${index}.${field.path}`,
              profile: field.profile,
            })
          }
        })
      })
    })
  })
  return references
}

/**
 * The same media reference with nothing chosen.
 *
 * Every key the reference already had is kept and emptied, rather than a fixed
 * trio written back: a reference carries a crop and a focus point beside the
 * asset ids, and a field that gains another one later should not need this
 * function edited to be cleared properly.
 */
function emptyMedia(current) {
  if (!current || typeof current !== 'object') {
    return { sourceAssetId: null, renditionAssetId: null, crop: null }
  }
  return Object.fromEntries(Object.keys(current).map((key) => [key, null]))
}

/**
 * The same configuration with every picture taken out.
 *
 * Generation merges rather than replaces, so that a run which returns no logo
 * does not erase an uploaded one. That is right for a second run on the same
 * advertiser and wrong for a different one: the pictures, the asset registry
 * and the approved set all belong to the advertiser they were gathered for,
 * and a merge would leave the new ad wearing the old one's photographs
 * wherever the new run happens to supply nothing.
 *
 * Only what is bound to that advertiser goes: the image fields, the asset
 * records behind them, and the suggestions. The headline, the colours and
 * every other field are left for the merge to overwrite as usual.
 */
export function clearImageSelections(schema, configuration) {
  if (!configuration) return configuration

  let next = cloneValue(configuration)
  imageFieldReferences(schema || {}, next).forEach(({ path }) => {
    next = setAtPath(next, path, emptyMedia(getAtPath(next, path)))
  })
  next = setAtPath(next, 'assets', {})
  next = setAtPath(next, 'suggestions', {})
  return next
}

export function applyPreset(configuration, preset) {
  let next = configuration
  ;(preset?.operations || []).forEach((operation) => {
    if (operation.op === 'set') next = setAtPath(next, operation.path, cloneValue(operation.value))
  })
  return setAtPath(next, 'presetId', preset?.id || null)
}

export function formatConfiguration(format) {
  return {
    id: format.id,
    width: Number(format.width),
    height: Number(format.height),
    bannerFormatAlias: format.bannerFormatAlias,
  }
}

export function newCollectionIdentity(prefix = 'item') {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() || Date.now()}`
}

// Collection list changes shared by the editor and the template review: each returns the next
// items and the index to select, or null when the collection's limits forbid the change.
export function withAddedItem(items, collection) {
  if (items.length >= collection.maxItems) return null
  const item = setAtPath(cloneValue(collection.itemDefaults), collection.identityPath, newCollectionIdentity(collection.id))
  return { items: [...items, item], selected: items.length }
}

export function withoutItem(items, collection, index) {
  if (items.length <= collection.minItems) return null
  const next = items.filter((_, itemIndex) => itemIndex !== index)
  return { items: next, selected: Math.min(index, next.length - 1) }
}

export function withMovedItem(items, from, to) {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return null
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return { items: next, selected: to }
}

export function withDuplicatedItem(items, collection, index) {
  if (!items[index] || items.length >= collection.maxItems) return null
  const duplicate = setAtPath(cloneValue(items[index]), collection.identityPath, newCollectionIdentity(collection.id))
  const next = [...items]
  next.splice(index + 1, 0, duplicate)
  return { items: next, selected: index + 1 }
}

/**
 * Expands the AI-schema response shape into a configuration.
 *
 * The generator is now asked with the compiled AI schema — a flat list of paths — so it may
 * answer in the same vocabulary: `{ values: { "<path>": value }, collections: { "<path>": [ { "<relative path>": value } ] } }`.
 * A full configuration (what it returned before, and what it may keep returning) passes through
 * untouched, recognised by the absence of both keys.
 *
 * Collection rows are expanded onto the item that is already there, so fields the schema declares
 * but the generator left out — identities, an uploaded image — are not lost. Identities are
 * repaired when rows are added, because two items sharing one id break selection and reordering.
 */
export function expandAiSchemaValues(schema, generated, current) {
  if (!generated || typeof generated !== 'object') return generated
  const hasValues = generated.values && typeof generated.values === 'object'
  const hasCollections = generated.collections && typeof generated.collections === 'object'
  if (!hasValues && !hasCollections) return generated

  let expanded = {}
  if (hasValues) {
    Object.entries(generated.values).forEach(([path, value]) => {
      expanded = setAtPath(expanded, path, cloneValue(value))
    })
  }

  if (hasCollections) {
    ;(schema?.collections || []).forEach((collection) => {
      const rows = generated.collections[collection.path]
      if (!Array.isArray(rows)) return
      const existing = getAtPath(current, collection.path)
      const identityPath = collection.identityPath || 'id'
      const defaultIdentity = String(getAtPath(collection.itemDefaults || {}, identityPath) || '')
      const prefix = defaultIdentity.replace(/-?\d+$/, '') || collection.id || 'item'
      const seen = new Set()

      const items = rows.map((row, index) => {
        let item = cloneValue(
          (Array.isArray(existing) && existing[index]) || collection.itemDefaults || {},
        )
        if (row && typeof row === 'object') {
          Object.entries(row).forEach(([path, value]) => {
            item = setAtPath(item, path, cloneValue(value))
          })
        }
        const identity = String(getAtPath(item, identityPath) || '')
        if (!identity || seen.has(identity)) {
          item = setAtPath(item, identityPath, `${prefix}-${index + 1}`)
        }
        seen.add(String(getAtPath(item, identityPath)))
        return item
      })
      expanded = setAtPath(expanded, collection.path, items)
    })
  }

  // Envelope keys no schema declares, carried exactly as a full configuration would carry them.
  ;['assets', 'suggestions'].forEach((key) => {
    if (generated[key] && typeof generated[key] === 'object') {
      expanded[key] = cloneValue(generated[key])
    }
  })

  return expanded
}

/**
 * Applies an AI-generated configuration onto the current one through the template schema.
 *
 * The generator is an external service that receives the current configuration and
 * returns a whole new one. Replacing wholesale trusted it to know every path of every
 * template; when it does not (Package V2 templates keep their content under `data.*`),
 * those fields silently fell back to defaults — the brand logo being the visible casualty.
 *
 * The generator may answer either with a full configuration or with the flat AI-schema shape;
 * `expandAiSchemaValues` normalises the latter first. Only paths the schema declares are copied. Image references are taken only when the
 * generator actually supplies a source asset, so an existing upload survives a run that
 * returns none. Collections are replaced whole when present, and generated asset records
 * are merged so their preview URLs still resolve. Identity (`template`, `format`,
 * `schemaVersion`) always stays with the editor.
 *
 * `suggestions` is carried across for the same reason as `assets`: it is not a field any
 * schema declares, so the walk above cannot see it, yet it is the whole of what the run
 * produced beyond the one option it picked — every approved image and every alternative
 * headline and call to action. Replaced rather than merged, because a stale set belongs
 * to the previous run.
 */
// Plain text written into a rich-inline field (3.2.0 button labels) must survive being read as
// HTML: bare &, < and > are escaped; strings that already carry markup or entities are kept.
export function escapePlainRichText(value) {
  if (typeof value !== 'string' || /<[a-z/!][^>]*>/i.test(value)) return value
  return value
    .replace(/&(?!(?:[a-z][a-z0-9]*|#\d+|#x[0-9a-f]+);)/gi, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function escapePlainInlineRichText(schema, configuration) {
  let next = configuration
  const inline = (field) => field?.type === 'rich-text' && field.validation?.allowedMarkup === 'rich-inline'
  ;(schema?.sections || []).forEach((section) => {
    ;(section.fields || []).filter(inline).forEach((field) => {
      const value = getAtPath(next, field.path)
      const escaped = escapePlainRichText(value)
      if (escaped !== value) next = setAtPath(next, field.path, escaped)
    })
  })
  ;(schema?.collections || []).forEach((collection) => {
    const fields = (collection.itemSections || []).flatMap((section) => section.fields || []).filter(inline)
    const items = getAtPath(next, collection.path)
    if (!fields.length || !Array.isArray(items)) return
    items.forEach((_, index) => fields.forEach((field) => {
      const path = `${collection.path}.${index}.${field.path}`
      const value = getAtPath(next, path)
      const escaped = escapePlainRichText(value)
      if (escaped !== value) next = setAtPath(next, path, escaped)
    }))
  })
  return next
}

export function mergeGeneratedConfiguration(schema, current, generated) {
  if (!current) return generated
  if (!generated || typeof generated !== 'object') return current
  const source = expandAiSchemaValues(schema, generated, current)
  const missing = Symbol('omitted generated field')
  let next = cloneValue(current)
  ;(schema?.sections || []).forEach((section) => {
    ;(section.fields || []).forEach((field) => {
      const value = getAtPath(source, field.path, missing)
      if (value === missing) return
      if (field.type === 'image-editor') {
        if (value && value.sourceAssetId) next = setAtPath(next, field.path, cloneValue(value))
        return
      }
      next = setAtPath(next, field.path, cloneValue(value))
    })
  })
  ;(schema?.collections || []).forEach((collection) => {
    const items = getAtPath(source, collection.path)
    if (Array.isArray(items)) next = setAtPath(next, collection.path, cloneValue(items))
  })
  if (source.assets && typeof source.assets === 'object') {
    next = setAtPath(next, 'assets', { ...(next.assets || {}), ...cloneValue(source.assets) })
  }
  if (source.suggestions && typeof source.suggestions === 'object') {
    next = setAtPath(next, 'suggestions', cloneValue(source.suggestions))
  }
  return escapePlainInlineRichText(schema, next)
}
